import { notification } from 'antd'
import axios, { AxiosResponse } from 'axios'
import { camelCase, omit } from 'lodash'
import { useIntl, FormattedMessage } from 'react-intl'
import React, { useState, createContext, useEffect } from 'react'
import { isExpired } from 'react-jwt'
import { AuthContextProps, AuthProviderProps, LoginError, LoginResponse, AuthUser } from './types'
import { apiUrl } from '../../globals'
import { UserRole } from '../User/types'

const defaultUser = {
  id: 0,
  email: '',
  firstName: '',
  lastName: '',
  role: UserRole.PARTICIPANT,
  profilePicture: '',
}

export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  hasLoginError: null,
  authUser: defaultUser,
  translateUserRole: (role: UserRole) => <>{role}</>,
  login: () => void 0,
  logout: () => void 0,
})

const initAxiosInstance = () => {
  const authToken = localStorage.getItem('authToken')

  if (authToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
  }
}

const getInitialUser = (): AuthUser => {
  const userStr = localStorage.getItem('user')

  return userStr ? JSON.parse(userStr) : defaultUser
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  initAxiosInstance()

  const intl = useIntl()
  const alreadyHasToken = localStorage.getItem('authToken') !== null

  const [ isAuthenticated, setIsAuthenticated ] = useState(alreadyHasToken || false)
  const [ hasLoginError, setHasLoginError ] = useState<null | Error>(null)
  const [ authUser, setAuthUser ] = useState<AuthUser>(getInitialUser())

  const login = (email: string, password: string) => {
    axios.post(`${apiUrl}/authenticate`, { email, password })
      .then((res: AxiosResponse<LoginResponse>) => {
        localStorage.setItem('authToken', res.data.token)

        setHasLoginError(null)
        setIsAuthenticated(true)
      
        const currentUser = omit(res.data, 'token')
        setAuthUser(currentUser)
        localStorage.setItem('user', JSON.stringify(currentUser))
      })
      .catch((err) => {
        if (!err.response) {
          setHasLoginError(new Error(LoginError.NO_CONNECTION))
        }
 
        if (err.response.status === 401) {
          setHasLoginError(new Error(LoginError.INVALID_USERNAME_OR_PASSWORD))
        }
      })
  }

  const translateUserRole = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return <FormattedMessage id='user.role.admin' />

      case UserRole.INSTRUCTOR:
        return <FormattedMessage id='user.role.instructor' />

      case UserRole.PARTICIPANT:
        return <FormattedMessage id='user.role.participant' />

      case UserRole.CLIENT:
        return <FormattedMessage id='user.role.client' />
    }
  }

  const logout = () => {
    window.location.href = '/'
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    delete axios.defaults.headers.common['Authorization']
    setIsAuthenticated(false)
  }

  // Check if token is valid on initial page load and watch out for expiry of token
  useEffect(() => {
    const authToken = localStorage.getItem('authToken')

    if (authToken && !isExpired(authToken)) {
      setIsAuthenticated(true)
    } else if (authToken && isExpired(authToken)) {
      localStorage.removeItem('authToken')

      setHasLoginError(new Error(LoginError.SESSION_EXPIRED))
      setIsAuthenticated(false)
    } else {
      setIsAuthenticated(false)
    }
  }, [])

  useEffect(() => {
    if (hasLoginError) {
      if (hasLoginError.message === LoginError.SESSION_EXPIRED) {
        notification.open({
          message: intl.formatMessage({ id: 'login.notify' }),
          description: intl.formatMessage({ id: `login.error.${camelCase(hasLoginError.message)}` }),
          type: 'warning',
          placement: 'bottomRight',
        })
      } else {
        notification.open({
          message: intl.formatMessage({ id: 'login.error' }),
          description: intl.formatMessage({ id: `login.error.${camelCase(hasLoginError.message)}` }),
          type: 'error',
          placement: 'bottomRight',
        })
      }

    }
  }, [ hasLoginError ])

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      hasLoginError,
      authUser,
      translateUserRole,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

